import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import API from '../api/api';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiAlert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import TranslateOutlined from '@mui/icons-material/TranslateOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { List, AutoSizer } from 'react-virtualized';
import 'react-virtualized/styles.css';

// The one dialog between the word count and the paid machine translation request: the field
// table with per-row ticks, the word ledger, the overwrite and HTML notices and the
// quota-dependent actions. The field list opens it per language, the size chart's update run
// for all affected languages at once (counts aggregated, a language column in the keys).

// the key column of the field table. Sections with intricate keys (theme, metafields, partner
// apps) show a key icon on every row - the full key on hover, a click copies it; the other
// sections write the key out. Decided per section, never mixed within a table
export const ICON_KEY_CONTEXTS = ['theme', 'static', 'shop_metafields', 'apps'];

// sections whose fields carry no key worth a column: a navigation link's or custom content's id
// says nothing about it
export const NO_KEY_CONTEXTS = ['navigation', 'custom'];

export const FieldKeyCell = ({ fieldKey, iconOnly, t }) => {
  const [copied, setCopied] = React.useState(false);
  const key = String(fieldKey || '');
  if(!iconOnly) {
    return <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{key}</Box>;
  }
  const copy = (e) => {
    e.stopPropagation();
    if(navigator.clipboard && key) navigator.clipboard.writeText(key).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Tooltip title={copied ? t('system.copied') : <span style={{ wordBreak: 'break-all' }}>{key}</span>} arrow>
      <KeyOutlinedIcon onClick={copy} sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'text.primary' } }} />
    </Tooltip>
  );
};

// a table cell's worth of a field's text: tags stripped, 50 characters
export const excerptOf = (source) => {
  const text = String(source ?? '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 50 ? text.slice(0, 50).trimEnd() + '…' : text;
};

// Glossary marks: the dialog fetches the glossaries itself, so no caller carries glossary logic.
// Entries are per target language, so a row is matched against the glossary of its own language:
// rows name theirs in `languageId` (the size chart's run over several languages), rows without
// one belong to the edit language. Lists are kept per language for the session and shown at once
// while a fresh one loads; only shops with the feature ask
const NO_TERMS = [];
const glossaryTermsByLanguage = new Map();
const hasGlossaryFeature = (shop) =>
  !!(shop?.langify_features || []).find(x => x.name === 'glossary' && x.is_enabled === true);
// {source, target, keep} per entry that triggers for the given language: the route lists every
// entry of the shop with the language's target or null, so a term translated for German alone
// must not mark a French row. "Keep" entries hold no target and apply to every language
const glossaryTermsFrom = (entries, languageId) => (entries || []).map(entry => {
  const source = String(entry.text || entry.source || '').trim();
  const target = entry.translations ? (entry.translations[languageId] || '') : (entry.target || '');
  return { source, target: String(target || '').trim(), keep: !!(entry.type && entry.type !== 'translate') };
}).filter(term => term.source !== '' && (term.keep || term.target !== ''));
// whole words, case-insensitive, on the plain text; null without terms
const glossaryRegexOf = (terms) => {
  const sources = terms.map(term => term.source).filter(Boolean);
  if(sources.length === 0) return null;
  const escaped = [...sources].sort((a, b) => b.length - a.length).map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  try { return new RegExp(`(?<![\\p{L}\\p{N}])(?:${escaped.join('|')})(?![\\p{L}\\p{N}])`, 'giu'); } catch(e) { return null; }
};
// -> {termsOf(languageId), regexOf(languageId), any}; `any` says whether a mark column is needed
const useGlossary = (rows) => {
  const shop = useSelector(state => state.shop?.shop);
  const editLanguageId = shop?.edit_language;
  const enabled = hasGlossaryFeature(shop) && !!editLanguageId;
  const languageKey = [...new Set((rows || []).map(row => String(row.languageId ?? editLanguageId)))].sort().join(',');
  const [byLanguage, setByLanguage] = React.useState(() => new Map());
  React.useEffect(() => {
    if(!enabled) {
      setByLanguage(new Map());
      return undefined;
    }
    let live = true;
    const ids = languageKey === '' ? [] : languageKey.split(',');
    setByLanguage(new Map(ids.map(id => [id, glossaryTermsByLanguage.get(id) || NO_TERMS])));
    ids.forEach(id => API.getGlossary(id, res => {
      if(!live || res?.success !== true) return;
      const terms = glossaryTermsFrom(res.glossary, id);
      glossaryTermsByLanguage.set(id, terms);
      setByLanguage(current => new Map(current).set(id, terms));
    }));
    return () => { live = false; };
  }, [enabled, languageKey]);
  return React.useMemo(() => {
    const regexes = new Map([...byLanguage].map(([id, terms]) => [id, glossaryRegexOf(terms)]));
    const keyOf = (languageId) => String(languageId ?? editLanguageId);
    return {
      termsOf: (languageId) => byLanguage.get(keyOf(languageId)) || NO_TERMS,
      regexOf: (languageId) => regexes.get(keyOf(languageId)) || null,
      any: [...regexes.values()].some(Boolean),
    };
  }, [byLanguage, editLanguageId]);
};

/**
 * data: {fieldsNum, wordsNum, availableWords, missingNum, partFieldsNum, overwriteNum, hasMuchHtml,
 *   fieldSummary: [{id, fullKey, language?, source, target, replaces, suggestedSkip, skipReason, skipHint?}]}
 *   skipReason 'service' is the translation service's own skip (skipHint = its reason); handle/productType carry no mark
 * excluded: Set of unticked row ids; onToggleField(id) - the caller recounts (recounting)
 * languageName: the target language for the subtitle, null for a run over several languages
 * dismissable + dismissed + onToggleDismissed: the "don't show again" tick
 * onConfirm / onTranslateAvailable (the part that fits the quota) / onGetMoreWords / onClose
 * alternatives: {filled, outdated} counts of the other kinds of run when this one has nothing to
 *   send; onRunSelection(selection) starts one of them (the dropdown's entry point)
 */
const MachineTranslationDialog = ({
  data: d, excluded, onToggleField, recounting = false, languageName = null, iconKeys = false,
  defaultFieldsOpen = false, dismissable = false, dismissed = false, onToggleDismissed, hideKeys = false,
  onClose, onConfirm, onTranslateAvailable, onGetMoreWords, alternatives = null, onRunSelection,
}) => {
  const { t } = useTranslation();
  const [fieldsOpen, setFieldsOpen] = React.useState(defaultFieldsOpen);
  // a row is matched the first time it renders and remembered - the list is virtualized, so
  // thousands of rows cost nothing until they scroll into view
  const glossary = useGlossary(d?.fieldSummary);
  const glossaryRegex = glossary.any;
  const glossaryCache = React.useRef(new Map());
  React.useEffect(() => { glossaryCache.current = new Map(); }, [glossary]);
  const glossaryMatches = (line) => {
    const regex = glossary.regexOf(line.languageId);
    if(!regex) return null;
    const key = String(line.id);
    if(glossaryCache.current.has(key)) return glossaryCache.current.get(key);
    const terms = glossary.termsOf(line.languageId);
    const text = String(line.source || '');
    const found = new Map();
    let m;
    regex.lastIndex = 0;
    while((m = regex.exec(text)) !== null) {
      const hit = m[0].toLowerCase();
      if(!found.has(hit)) {
        const term = terms.find(candidate => candidate.source.toLowerCase() === hit);
        if(term) found.set(hit, term);
      }
      if(m[0].length === 0) regex.lastIndex++;
    }
    const result = found.size > 0 ? [...found.values()] : null;
    glossaryCache.current.set(key, result);
    return result;
  };
  // a fixed-width slot like the other marks, so texts line up whether or not a row has a hit. The
  // hover says a glossary entry was found, not which: an entry can be a whole sentence, and several
  // of them would make a tooltip nobody reads
  const glossaryMark = (line) => {
    const hits = glossaryMatches(line);
    return markSlot(hits &&
      <Tooltip title={t('itemsList.glossaryMatchHint', { count: hits.length })} arrow>
        <BookmarkBorderOutlinedIcon sx={{ fontSize: 16, color: 'info.main' }} />
      </Tooltip>
    );
  };
  if(!d) return null;
  const outQuota = d.missingNum > 0;
  const offersAlternatives = !outQuota && d.fieldsNum === 0 && !!alternatives && !!onRunSelection && (alternatives.outdated > 0 || alternatives.filled > 0);
  // a plain run fills empty fields only: no target column, no need for the wide dialog
  const hasTargets = d.fieldSummary.some(line => line.target);
  // ids compare as strings: sections hand numeric field ids, the exclusion sets hold strings
  const excludedKeys = new Set([...excluded].map(String));
  const isExcludedRow = (line) => excludedKeys.has(String(line.id));
  // rows that stay out, of either kind: left out by default (a tick brings them in) or not
  // translatable by the service. One sentence, deliberately vague - the rows below tell the rest
  // rows without a tick: the service's skips and Shopify references; the default skips count while unticked
  const hardSkipped = (line) => line.skipReason === 'service' || line.skipReason === 'reference';
  const skippedNow = d.fieldSummary.filter(line => hardSkipped(line) || (line.suggestedSkip && isExcludedRow(line))).length;
  // the mark slots have a fixed width so the texts line up whether or not a row carries a mark;
  // the source slot only exists when some row can carry one
  const anySkipMark = d.fieldSummary.some(hardSkipped);
  // the help behind the skip sentence names only the cases this table holds: the service's skips,
  // and the section's default skips (handle: products, collections, pages, blogs, articles;
  // product type: products only)
  const skipHelp = {
    service: d.fieldSummary.some(line => line.skipReason === 'service'),
    reference: d.fieldSummary.some(line => line.skipReason === 'reference'),
    handle: d.fieldSummary.some(line => line.skipReason === 'handle'),
    productType: d.fieldSummary.some(line => line.skipReason === 'productType'),
  };
  // the hover shows the service's reason alone, capitalised ("Number (range)"); that the row is
  // not sent is already said by its grey, unticked state
  const reasonText = (reason) => { const r = String(reason || '').trim(); return r ? r.charAt(0).toUpperCase() + r.slice(1) : ''; };
  const markSlot = (mark) => <Box component="span" sx={{ width: 16, height: 16, display: 'inline-flex', flexShrink: 0 }}>{mark}</Box>;
  const number = (n) => Number(n).toLocaleString();
  const ROW_HEIGHT = 24;
  const LIST_HEIGHT = 220;
  // the icon-key column is as narrow as the word "Key" (its header) allows: 36px with 4px side padding
  const iconOnlyKeys = iconKeys && !d.fieldSummary.some(line => line.language);
  const keyCol = { flex: `0 0 ${iconKeys ? (iconOnlyKeys ? 36 : 150) : 110}px`, minWidth: 0 };
  const keyPad = iconOnlyKeys ? 0.5 : 0.75;
  // a section without keys drops the column; a multi-language run keeps it for its language names
  const showKeyCol = !hideKeys || d.fieldSummary.some(line => line.language);
  const textCol = { flex: '1 1 0', minWidth: 0 };
  const tickCol = { flex: '0 0 30px' };
  const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
  const cell = (extra = {}) => ({ display: 'flex', alignItems: 'center', minWidth: 0, px: 0.75, height: '100%', ...extra });
  const renderRow = (line, key, style) => {
    // a reference row is never in the exclusion set (it was never a candidate), it is dimmed all the same
    const isExcluded = isExcludedRow(line) || line.skipReason === 'reference';
    const dim = isExcluded ? { opacity: 0.45 } : {};
    return (
      <Box key={key} style={style} sx={{ display: 'flex', alignItems: 'stretch', borderRadius: '4px', '&:hover': { bgcolor: 'action.hover' } }}>
        {showKeyCol &&
          <Box component="span" sx={cell({ ...keyCol, px: keyPad, ...dim })}>
            {line.fullKey && <FieldKeyCell fieldKey={line.fullKey} iconOnly={iconKeys} t={t} />}
            {/* a run over several languages names the row's language next to the key */}
            {line.language && <Box component="span" sx={{ color: 'text.secondary', ml: 0.75, ...ellipsis }}>{line.language}</Box>}
          </Box>
        }
        <Box component="span" sx={cell({ ...textCol, color: 'text.primary', gap: 0.5, ...dim })}>
          {/* the mark holds whether or not the merchant ticks the row in. Only the translation
              service's skips carry one; handles and product types are simply left out by default */}
          {anySkipMark && markSlot(hardSkipped(line) &&
            <Tooltip title={line.skipReason === 'reference' ? t('itemsList.confirmTranslationReferenceHint') : reasonText(line.skipHint)} arrow>
              <HighlightOffIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            </Tooltip>
          )}
          {glossaryRegex && glossaryMark(line)}
          <Box component="span" sx={ellipsis}>{line.source}</Box>
        </Box>
        {hasTargets &&
          <Box component="span" sx={cell({ ...textCol, gap: 0.5, ...dim })}>
            {/* unticked, nothing is replaced - the mark goes with the tick */}
            {markSlot(line.replaces && !isExcluded &&
              <Tooltip title={t('itemsList.confirmTranslationReplaces')} arrow>
                <PriorityHighIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              </Tooltip>
            )}
            <Box component="span" sx={ellipsis}>{line.target}</Box>
          </Box>
        }
        {/* unticked: left out of the run, the numbers below recount. A row the service skips has
            no tick at all: it would go out and come back untouched either way; the section's
            default skips (handle, product type) stay a choice */}
        <Box component="span" sx={cell({ ...tickCol, justifyContent: 'center', px: 0, ...(hardSkipped(line) ? dim : {}) })}>
          {!hardSkipped(line) &&
            <Checkbox
              size="small"
              sx={{ p: 0.25, '&.Mui-checked .MuiSvgIcon-root': { backgroundImage: 'linear-gradient(#fff, #fff)', backgroundSize: '12px 12px', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' } }}
              checked={!isExcluded}
              onChange={() => onToggleField(line.id)}
            />
          }
        </Box>
      </Box>
    );
  };
  // the translations the run would replace first (the rows worth a second look), then the
  // untranslated ones, the flagged rows (unticked by default) last; by the rows' nature, not by
  // their ticks, so nothing jumps on a click
  const tier = (line) => (line.suggestedSkip ? 2 : (line.replaces ? 0 : 1));
  const rows = [...d.fieldSummary].sort((a, b) => tier(a) - tier(b));
  const hasLanguages = rows.some(line => line.language);
  // a header never runs into its neighbour: the key column is 28px wide in icon-key sections
  const head = { color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const ledgerRow = (label, value, sx = {}) => (
    <React.Fragment>
      <Box component="span">{label}</Box>
      <Box component="span" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...sx }}>{value}</Box>
    </React.Fragment>
  );
  // sm, not xs: the three quota actions must fit on one row without wrapping their labels
  return (
    <Dialog
      maxWidth={fieldsOpen && hasTargets ? 'md' : 'sm'}
      fullWidth
      // sm and md are fixed pixel widths, so the paper can animate between them
      slotProps={{ paper: { sx: { transition: 'max-width 300ms ease' } } }}
      onClose={onClose}
      aria-labelledby="translate-dialog-title"
      open
    >
      <DialogTitle id="translate-dialog-title" sx={{ pr: 6 }}>
        <Typography variant="h5" component="span">{t(outQuota ? 'itemsList.notEnoughWordsLeft' : 'itemsList.confirmTranslationTitle')}</Typography>
        {/* the same close as the Cancel button, where every other popup has it */}
        <IconButton aria-label={t('system.close')} onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* body1 like the ledger below: the count sentence and "Words available" read as one block */}
        <Typography variant="body1" color="text.secondary" component="div" sx={{ mb: 2 }}>
          {/* a tick on a default-skipped row turns "nothing to translate" into the count: the two
              sentences fade into each other instead of swapping */}
          <Fade in key={d.fieldsNum === 0 ? 'none' : 'count'} timeout={250} appear={false}>
            <Box component="span">
              {d.fieldsNum === 0
                ? <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 'bold', color: 'text.primary' }}>
                    {/* the one outcome that needs no action: said loudly, everything else in the dialog is grey */}
                    <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                    {t('itemsList.confirmTranslationSubtitleNone')}
                  </Box>
                : (languageName
                  ? t('itemsList.confirmTranslationSubtitle', { count: d.fieldsNum, language: languageName })
                  : t('itemsList.confirmTranslationSubtitleNoLanguage', { count: d.fieldsNum }))}
            </Box>
          </Fade>
          {/* right under the count, before the table: the skips explain the numbers. In a Collapse
              because a tick on a flagged row takes the sentence away while the ledger grows in */}
          <Collapse in={skippedNow > 0} timeout={250}>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, mb: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('itemsList.confirmTranslationSkipped', { count: skippedNow })}
              {/* the sentence stays vague on purpose; the why sits behind the question mark */}
              <Tooltip
                arrow
                placement="top"
                title={
                  <React.Fragment>
                    {skipHelp.service &&
                      <Box component="p" sx={{ m: 0 }}>{t('itemsList.confirmTranslationSkippedHelpService')}</Box>
                    }
                    {skipHelp.reference &&
                      <Box component="p" sx={{ m: 0, '& + &': { mt: 1 } }}>{t('itemsList.confirmTranslationSkippedHelpReference')}</Box>
                    }
                    {(skipHelp.handle || skipHelp.productType) &&
                      <Box component="p" sx={{ m: 0, '& + &': { mt: 1 } }}>
                        {t(skipHelp.handle && skipHelp.productType ? 'itemsList.confirmTranslationSkippedHelpDefaultsBoth'
                          : (skipHelp.handle ? 'itemsList.confirmTranslationSkippedHelpDefaultsHandle' : 'itemsList.confirmTranslationSkippedHelpDefaultsProductType'))}
                      </Box>
                    }
                  </React.Fragment>
                }
              >
                <HelpOutlineIcon sx={{ fontSize: 16, cursor: 'help', color: 'text.secondary', '&:hover': { color: 'text.primary' } }} />
              </Tooltip>
            </Typography>
          </Collapse>
          {/* the toggle sits on a divider, like the size chart's update notice; a second divider
              closes the open table off against the ledger. No toggle when the table has no rows:
              "everything is translated" needs no empty table under it */}
          {rows.length > 0 &&
            <Divider textAlign="right" sx={{ mt: 1 }}>
              <Button size="small" variant="text" sx={{ p: 0, minWidth: 0, textTransform: 'none' }} onClick={() => setFieldsOpen(!fieldsOpen)}>
                {t(fieldsOpen ? 'itemsList.confirmTranslationHideFields' : 'itemsList.confirmTranslationShowFields')}
              </Button>
            </Divider>
          }
          <Collapse in={fieldsOpen && rows.length > 0}>
            {/* three columns: key (hover: full key, click: copy), source, target - the target
                gets an exclamation mark where the run replaces it. The dialog widens to md
                while this is open (see maxWidth) */}
            {/* a virtualized list: a theme brings thousands of rows, only the visible ones exist.
                Rows are positioned by the list, so the columns have fixed widths instead of
                sharing one grid; the header uses the same widths */}
            <Box sx={{ mt: 1, fontSize: 13 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {showKeyCol &&
                  <Box component="span" sx={{ ...head, ...keyCol, pl: iconKeys && hasLanguages ? '28px' : keyPad, pr: keyPad, py: 0.25 }}>{t(hasLanguages ? 'itemsList.fieldTableLanguage' : 'itemsList.fieldTableKey')}</Box>
                }
                <Box component="span" sx={{ ...head, ...textCol, pl: `${6 + (anySkipMark ? 20 : 0) + (glossaryRegex ? 20 : 0)}px`, pr: 0.75, py: 0.25 }}>{t('itemsList.fieldTableSource')}</Box>
                {hasTargets && <Box component="span" sx={{ ...head, ...textCol, pl: `${6 + 20}px`, pr: 0.75, py: 0.25 }}>{t('itemsList.fieldTableTarget')}</Box>}
                <Box component="span" sx={tickCol} />
              </Box>
              <AutoSizer disableHeight>
                {({ width }) => (
                  <List
                    width={width}
                    height={Math.min(LIST_HEIGHT, rows.length * ROW_HEIGHT)}
                    rowCount={rows.length}
                    rowHeight={ROW_HEIGHT}
                    overscanRowCount={10}
                    rowRenderer={({ index, key, style }) => renderRow(rows[index], key, style)}
                  />
                )}
              </AutoSizer>
            </Box>
            <Divider sx={{ mt: 1 }} />
          </Collapse>
        </Typography>
        {/* the ledger only where something would be spent: with nothing to translate the words do not
            move. Ticking a default-skipped row in turns it on, so it grows in rather than jumping */}
        <Collapse in={d.fieldsNum > 0} timeout={250}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 3, rowGap: 0.5, px: 0.75, py: 0.5 }}>
            {ledgerRow(t('itemsList.confirmTranslationAvailable'), number(d.availableWords))}
            {ledgerRow(t('itemsList.confirmTranslationNeeded'), recounting ? <CircularProgress size={12} /> : '-' + number(d.wordsNum), { color: 'error.main' })}
            <Divider sx={{ gridColumn: '1 / -1', my: 0.5 }} />
            {outQuota
              ? ledgerRow(t('itemsList.confirmTranslationMissing'), number(d.missingNum), { color: 'error.main', fontWeight: 'bold' })
              : ledgerRow(t('itemsList.confirmTranslationLeft'), number(d.availableWords - d.wordsNum), { fontWeight: 'bold' })}
          </Box>
        </Collapse>
        {d.overwriteNum > 0 &&
          <MuiAlert severity="warning" sx={{ mt: 2 }}>{t('itemsList.confirmTranslationOverwrite', { count: d.overwriteNum })}</MuiAlert>
        }
        {d.hasMuchHtml &&
          <MuiAlert severity="info" sx={{ mt: 2 }}>{t('itemsList.htmlInContentDescription')}</MuiAlert>
        }
      </DialogContent>
      {/* labels never break mid-word; when a language's three actions exceed the row, whole
          buttons wrap onto a second row, right-aligned */}
      <DialogActions sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, '& > :not(:first-of-type)': { ml: 0 }, '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
        {dismissable &&
          <FormControlLabel
            // MUI pads the checkbox 9px all round for its ripple and gives the label -11px; both
            // are taken out so the row starts where the actions' padding ends
            control={<Checkbox sx={{ pl: 0, pr: '6px' }} checked={dismissed} onChange={onToggleDismissed} />}
            style={{ marginRight: 'auto', marginLeft: 0 }}
            label={t('itemsList.confirmTranslationDontShowAgain')}
          />
        }
        {/* with nothing to translate, closing is the normal way out and the re-translations are the
            alternatives, so the emphasis flips */}
        <Button variant={offersAlternatives ? 'contained' : 'outlined'} color="primary" onClick={onClose} size="medium"
          sx={{ transition: 'background-color 250ms ease, color 250ms ease, border-color 250ms ease' }}>
          {t('system.cancel')}
        </Button>
        {outQuota ?
          <React.Fragment>
            {d.partFieldsNum > 0 && onTranslateAvailable &&
              <Button variant="outlined" onClick={onTranslateAvailable} size="medium" disabled={recounting}>
                {t('itemsList.translateAvailable')}
              </Button>
            }
            <Button variant="contained" color="primary" size="medium" onClick={onGetMoreWords}>
              <TranslateOutlined size={20} style={{marginRight: 8}} /> {t('autoTranslationsTile.getMoreWordsButton')}
            </Button>
          </React.Fragment>
        // a tick starts a recount: the alternatives stay where they are with a spinner layer over
        // them, so nothing moves and nothing can be clicked while the count is out
        : offersAlternatives ?
          <Fade in timeout={250} appear={false}><Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
            {/* the way forward stays in the dialog; both go through the normal count */}
            {alternatives.outdated > 0 &&
              <Button variant="outlined" size="medium" onClick={() => onRunSelection({ empty: true, outdated: true, filled: false })}>
                {t('itemsList.confirmTranslationRetranslateOutdated', { count: alternatives.outdated })}
              </Button>
            }
            {alternatives.filled > 0 &&
              <Button variant="outlined" size="medium" onClick={() => onRunSelection({ empty: true, outdated: false, filled: true })}>
                {t('itemsList.confirmTranslationRetranslateFilled', { count: alternatives.filled })}
              </Button>
            }
            {recounting &&
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'background.paper', opacity: 0.75, borderRadius: 1, cursor: 'progress' }}>
                <CircularProgress size={20} />
              </Box>
            }
          </Box></Fade>
        :
          <Fade in timeout={250} appear={false}>
            {/* a recount with fields already in the run: the button keeps its look and its label,
                a spinner layer over it says it is busy - the same as over the alternatives */}
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <Button variant="contained" color="primary" size="medium" onClick={onConfirm} disabled={d.fieldsNum === 0}>
                {t('itemsList.confirmTranslationRun')}
              </Button>
              {recounting &&
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'background.paper', opacity: 0.75, borderRadius: 1, cursor: 'progress' }}>
                  <CircularProgress size={20} />
                </Box>
              }
            </Box>
          </Fade>
        }
      </DialogActions>
    </Dialog>
  );
};

export default MachineTranslationDialog;
